import React from 'react';
import { format } from 'date-fns';
import {
    CalendarIcon,
    ClockIcon,
    MapPinIcon,
    VideoCameraIcon,
    EyeIcon,
    TrashIcon,
    UserCircleIcon,
    ChevronRightIcon,
    PencilSquareIcon
} from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { useAuth } from '../context/AuthContext';

const ExecutiveMeetingTable = ({ meetings, onView, onDelete, onRSVP, onManageMembers, onEdit, onAddVisitor, context }) => {
    const { user } = useAuth();
    const meetingLabel = user?.namingConvention?.meetingLabel || 'Meeting';
    const loggedInRole = user?.role;
    const activeRole = sessionStorage.getItem('activeRole') || loggedInRole;

    const isChapterAdmin = (loggedInRole === 'chapter_admin' || loggedInRole === 'super_admin') && activeRole !== 'member';

    const canEditOrDelete = (meeting) => {
        const meetingDate = new Date(meeting.meeting_date);
        meetingDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPast = meetingDate < today;

        if (isPast) return false; // Past meetings are read-only

        if (context === 'chapter') {
            // Chapter Meetings
            if (loggedInRole === 'admin') return false; // Org admin -> view only
            if (loggedInRole === 'chapter_admin' || loggedInRole === 'super_admin') {
                return activeRole !== 'member'; // Switched to member -> view only
            }
            return false; // Normal member -> view only
        } else {
            // Member Meetings (1-to-1)
            if (activeRole !== 'member') return false; // Shown in member login only
            return user && meeting.created_by === user.id; // Only creator has edit/delete
        }
    };

    const getStatusStyles = (rsvp) => {
        switch (rsvp) {
            case 'attending': return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
            case 'maybe': return 'bg-amber-50 text-amber-700 ring-amber-600/20';
            case 'not_attending': return 'bg-rose-50 text-rose-700 ring-rose-600/20';
            case 'invited': return 'bg-blue-50 text-blue-700 ring-blue-600/20';
            default: return 'bg-slate-50 text-slate-600 ring-slate-500/10';
        }
    };

    const safeFormat = (dateString, formatStr, fallbackDate = null) => {
        // Handle zero-dates from MySQL or empty strings
        if (!dateString || dateString === '0000-00-00 00:00:00') {
            if (fallbackDate) return safeFormat(fallbackDate, formatStr);
            return 'TBD';
        }
        
        let date;
        // Check if it's a time-only string (e.g., '14:30:00' or '14:30')
        if (typeof dateString === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(dateString)) {
            const [hours, minutes] = dateString.split(':');
            date = new Date();
            date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        } else {
            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) {
            if (fallbackDate) return safeFormat(fallbackDate, formatStr);
            return 'TBD';
        }
        return format(date, formatStr);
    };

    if (!meetings || meetings.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm text-slate-400">
                <CalendarIcon className="w-12 h-12 opacity-20 mb-3" />
                <p className="text-sm font-bold uppercase tracking-widest opacity-60">No {meetingLabel.toLowerCase()}s scheduled</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {meetings.map((meeting) => {
                const meetingDate = new Date(meeting.meeting_date);
                meetingDate.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isUpcomingOrToday = meetingDate >= today;

                return (
                    <div
                        key={meeting.id}
                        className="group relative bg-white border-2 border-slate-100 rounded-3xl p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                    >
                    <div>
                        {/* Header & Date Block */}
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col items-center justify-center group-hover:bg-white group-hover:border-primary-100 group-hover:shadow-sm transition-all flex-shrink-0">
                                <span className="text-[10px] font-black text-slate-400 uppercase leading-none mb-0.5">
                                    {safeFormat(meeting.meeting_date, 'MMM', meeting.created_at)}
                                </span>
                                <span className="text-xl font-black text-slate-900 leading-none">
                                    {safeFormat(meeting.meeting_date, 'dd', meeting.created_at)}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-black text-slate-900 truncate">
                                    {safeFormat(meeting.meeting_date, 'EEEE', meeting.created_at)}
                                </p>
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 mt-0.5">
                                    <ClockIcon className="w-3.5 h-3.5" />
                                    {safeFormat(meeting.start_time || meeting.meeting_date, 'hh:mm a', meeting.created_at)}
                                    {meeting.end_time && ` - ${safeFormat(meeting.end_time, 'hh:mm a', meeting.created_at)}`}
                                </div>
                            </div>
                        </div>

                        {/* Title & Organizer */}
                        <div className="mt-4">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                <h3 className="text-[16px] font-black text-slate-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                                    {meeting.title}
                                </h3>
                                {meeting.status && (
                                    <span className={twMerge(
                                        "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest whitespace-nowrap",
                                        meeting.status === 'Completed' ? "bg-emerald-100 text-emerald-700" :
                                            meeting.status === 'Not Completed' ? "bg-rose-100 text-rose-700" :
                                                "bg-blue-100 text-blue-700"
                                    )}>
                                        {meeting.status}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <UserCircleIcon className="w-4 h-4 text-slate-300" />
                                <span className="text-[12px] font-bold text-slate-400 truncate">
                                    Organized by: {meeting.created_by_name || meeting.creator_name}
                                </span>
                            </div>
                        </div>

                        {/* Mode & Venue details */}
                        <div className="grid grid-cols-1 gap-3 my-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className={twMerge(
                                    "p-2 rounded-xl border transition-all flex-shrink-0",
                                    meeting.meeting_link
                                        ? "bg-blue-50/50 border-blue-100 text-blue-600"
                                        : "bg-emerald-50/50 border-emerald-100 text-emerald-600"
                                )}>
                                    {meeting.meeting_link ? <VideoCameraIcon className="w-4 h-4" /> : <MapPinIcon className="w-4 h-4" />}
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[12px] font-black text-slate-900 truncate">
                                        {meeting.meeting_link ? 'Virtual Session' : (meeting.location || 'In-Person')}
                                    </span>
                                    {meeting.meeting_link && (
                                        <span className="text-[10px] font-bold text-slate-400 truncate max-w-[200px]" title={meeting.meeting_link}>
                                            {meeting.meeting_link}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* RSVP / Registration response selection */}
                        {isUpcomingOrToday && (
                            <div className="mb-4">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Your Response</p>
                                <div className="flex items-center p-1 bg-slate-100/80 rounded-2xl w-full border border-slate-200/50">
                                    {meeting.isRegistered !== undefined ? (
                                        <button
                                            onClick={() => onRSVP(meeting.id, 'attending')}
                                            className={twMerge(
                                                "w-full py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                                meeting.isRegistered
                                                    ? "bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100/50 font-black"
                                                    : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                                            )}
                                        >
                                            {meeting.isRegistered ? 'Registered' : 'Register'}
                                        </button>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-1 w-full">
                                            {['attending', 'maybe', 'not_attending'].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => onRSVP(meeting.id, s)}
                                                    title={s.replace('_', ' ')}
                                                    className={twMerge(
                                                        "py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all text-center",
                                                        meeting.user_rsvp === s
                                                            ? (s === 'attending' ? "bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100/50 font-black" :
                                                                s === 'maybe' ? "bg-white text-amber-600 shadow-sm ring-1 ring-amber-100/50 font-black" :
                                                                    "bg-white text-rose-600 shadow-sm ring-1 ring-rose-100/50 font-black")
                                                            : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                                                    )}
                                                >
                                                    {s === 'attending' ? 'Yes' : s === 'maybe' ? '?' : 'No'}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Actions Footer */}
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 mt-auto">
                        <button
                            onClick={() => onView(meeting)}
                            className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-primary-600 hover:bg-primary-50 hover:border-primary-200 transition-all shadow-sm active:scale-95"
                            title="View Details"
                        >
                            <EyeIcon className="w-4 h-4" />
                        </button>
                        {onManageMembers && (
                            <button
                                onClick={() => onManageMembers(meeting)}
                                className="px-4 py-2 bg-primary-50 border border-primary-100 rounded-xl text-[12px] font-black text-primary-600 hover:bg-primary-100 transition-all active:scale-95"
                            >
                                Members
                            </button>
                        )}
                        {isChapterAdmin && onAddVisitor && isUpcomingOrToday && (
                            <button
                                onClick={() => onAddVisitor(meeting)}
                                className="p-2.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 rounded-xl transition-all active:scale-95"
                                title="Add Visitor"
                            >
                                <UserCircleIcon className="w-4 h-4" />
                            </button>
                        )}
                        {canEditOrDelete(meeting) && onEdit && (
                            <button
                                onClick={() => onEdit(meeting)}
                                className="p-2.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 border border-slate-200 hover:border-amber-100 rounded-xl transition-all active:scale-95"
                                title={`Edit ${meetingLabel}`}
                            >
                                <PencilSquareIcon className="w-4 h-4" />
                            </button>
                        )}
                        {canEditOrDelete(meeting) && onDelete && (
                            <button
                                onClick={() => onDelete(meeting.id)}
                                className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 border border-slate-200 hover:border-rose-100 rounded-xl transition-all active:scale-95"
                                title={`Delete ${meetingLabel}`}
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            ); })}
        </div>
    );
};

export default ExecutiveMeetingTable;
